import {
  AlertTriangle,
  Clock3,
  FlaskConical,
  LoaderCircle,
  RotateCcw,
} from "lucide-react"
import { useEffect, useRef } from "react"

import { Button } from "@/components/ui/button"
import { useDemoResetStatus } from "@/lib/demo-reset-status"
import { cn } from "@/lib/utils"
import { RESET_MANIFEST } from "@/reset/manifest"

type DemoNoticeProps = {
  compact?: boolean
  onReset?: () => void
  resetting?: boolean
  message?: string
  tone?: "default" | "success" | "error"
  className?: string
  onGenerationChange?: (generation: number) => void
}

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
})

function ResetStateIcon({
  state,
}: {
  state: "scheduled" | "running" | "failed" | "overdue" | "unavailable"
}) {
  if (state === "running") {
    return <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />
  }
  if (["failed", "overdue", "unavailable"].includes(state)) {
    return <AlertTriangle className="size-3.5" aria-hidden="true" />
  }
  return <Clock3 className="size-3.5" aria-hidden="true" />
}

function statusDescription(
  state:
    "scheduled" | "running" | "failed" | "overdue" | "unavailable" | "checking"
) {
  switch (state) {
    case "running":
      return "Reset in progress — changes are temporarily paused."
    case "failed":
      return "Latest reset failed — the last successful fixture remains available."
    case "overdue":
      return "Reset delayed — the last successful fixture remains available."
    case "unavailable":
      return "Reset status unavailable — refresh before entering changes."
    case "checking":
      return "Checking the reset service."
    default:
      return "Reset service scheduled."
  }
}

function formatTimestamp(value: string) {
  return DATE_FORMATTER.format(new Date(value))
}

export function DemoNotice({
  compact = false,
  onReset,
  resetting = false,
  message,
  tone = "default",
  className,
  onGenerationChange,
}: DemoNoticeProps) {
  const { status, requestState } = useDemoResetStatus()
  const generation = status?.resetGeneration
  const maintenance = status?.state === "running"
  const state =
    requestState === "error" && !status
      ? "unavailable"
      : (status?.state ?? "checking")
  const previousGeneration = useRef<number | null>(null)

  useEffect(() => {
    if (generation === undefined || !onGenerationChange) return
    if (
      previousGeneration.current === null ||
      previousGeneration.current !== generation
    ) {
      previousGeneration.current = generation
      onGenerationChange(generation)
    }
  }, [generation, onGenerationChange])

  return (
    <div
      className={cn(
        "border-b border-[#b8dbe1] bg-[#eaf7f8] text-[#164b59]",
        maintenance && "border-[#e0c279] bg-[#fff8df] text-[#674b08]",
        ["failed", "overdue", "unavailable"].includes(state) &&
          "border-[#efc3c9] bg-[#fff0f2] text-[#8f2d3a]",
        tone === "success" && "border-[#b8dcc8] bg-[#eaf6ef] text-[#246947]",
        tone === "error" && "border-[#efc3c9] bg-[#fff0f2] text-[#8f2d3a]",
        className
      )}
      role={tone === "error" || state === "failed" ? "alert" : "status"}
      aria-live="polite"
      aria-atomic="true"
      data-testid="service-portal-demo-reset-notice"
    >
      <div
        className={cn(
          "mx-auto flex w-full flex-col gap-2 px-4 py-2 sm:px-6 lg:flex-row lg:items-center lg:justify-between",
          compact ? "min-h-9" : "min-h-10 max-w-[1440px]"
        )}
      >
        <div className="min-w-0 text-xs leading-5">
          <p className="flex flex-wrap items-center gap-x-2 font-semibold">
            <FlaskConical className="size-3.5 shrink-0" aria-hidden="true" />
            <span>Demo data reset</span>
            {state === "checking" ? (
              <LoaderCircle
                className="size-3.5 animate-spin"
                aria-hidden="true"
              />
            ) : (
              <ResetStateIcon state={state} />
            )}
            <span>{statusDescription(state)}</span>
          </p>
          <p className="mt-0.5 text-[0.7rem] leading-4 font-medium">
            Daily at {RESET_MANIFEST.schedule.localTime}{" "}
            {RESET_MANIFEST.schedule.timeZone} (
            {RESET_MANIFEST.schedule.utcOffset}).
            {status ? (
              <>
                {" "}
                Next:{" "}
                <time dateTime={status.nextScheduledResetAt}>
                  {formatTimestamp(status.nextScheduledResetAt)}
                </time>
                . Last completed:{" "}
                {status.lastSuccessfulResetAt ? (
                  <time dateTime={status.lastSuccessfulResetAt}>
                    {formatTimestamp(status.lastSuccessfulResetAt)}
                  </time>
                ) : (
                  "none yet"
                )}
                .
              </>
            ) : (
              " Next and last completion times are loading."
            )}{" "}
            Synthetic records are resettable; no external service or imagery
            delivery is connected. Do not enter sensitive information.
          </p>
          {message ? (
            <p className="mt-0.5 flex items-center gap-1.5 font-semibold">
              <span aria-hidden="true">•</span>
              {message}
            </p>
          ) : null}
        </div>
        {onReset ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onReset}
            disabled={resetting || maintenance}
            className="shrink-0 text-current hover:bg-white/70"
            aria-label={
              maintenance
                ? "Reset unavailable during daily demo reset"
                : resetting
                  ? "Resetting demo"
                  : "Reset demo data"
            }
            title={
              maintenance
                ? "Daily demo reset in progress"
                : resetting
                  ? "Resetting demo"
                  : "Reset demo data"
            }
          >
            <RotateCcw className={cn("size-3", resetting && "animate-spin")} />
            <span className="hidden sm:inline">
              {resetting ? "Resetting" : "Reset demo"}
            </span>
          </Button>
        ) : null}
      </div>
    </div>
  )
}
